import SearchIcon from "components/icons/SearchIcon";
import { MdAdd } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "auth/AuthProvider";
import { PERMISSIONS } from "auth/permissions";
import { useEffect, useRef, useState, ReactNode } from "react";
import { BiChevronDown } from "react-icons/bi";
import { CREATE_AGENT_CATEGORIES_OPTIONS, CREATE_AGENT_CATEGORIES } from "constants/agents";
import { usageApi } from "api/usage";
import TooltipHorizon from "components/tooltip";
import { useTranslation } from "hooks/useTranslation";

interface SearchProps {
  value: string;
  onChange: (value: string) => void;
  filter?: ReactNode;
  className?: string;
  children?: ReactNode;
}

const Search = ({ value, onChange, filter, className, children }: SearchProps) => {
  const { hasPermission } = useAuthContext();
  const navigate = useNavigate();
  const [isAgentLimitReached, setIsAgentLimitReached] = useState(false);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const permissions = {
    canCreateAgent: hasPermission(PERMISSIONS.CREATE_AGENT),
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Use setTimeout to ensure this runs after the click event that opened it
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleOptionClick = (type: CREATE_AGENT_CATEGORIES) => {
    setIsOpen(false);
    navigate(`/dashboard/agents/create?category=${type}`);
  };

  useEffect(() => {
    const fetchUsageData = async () => {
      if (!permissions.canCreateAgent) {
        setIsLoadingUsage(false);
        return;
      }

      try {
        const currentDate = new Date(Date.now()).toISOString().split("T")[0];
        const data = await usageApi.getUsageOverview({
          start_date: currentDate,
          end_date: currentDate,
        });

        if (
          data.total_agents !== null &&
          data.max_agents !== null &&
          data.total_agents >= data.max_agents
        ) {
          setIsAgentLimitReached(true);
        }
      } catch (err) {
        console.error("Error fetching usage data:", err);
        // Don't block the button if API fails
      } finally {
        setIsLoadingUsage(false);
      }
    };

    fetchUsageData();
  }, [permissions?.canCreateAgent]);

  return (
    <div className={`mx-auto mt-5 flex w-full gap-4 md:!gap-6 ${className || ""}`}>
      <div className="flex h-[45px] w-full rounded-xl border !border-gray-200 dark:!border-none dark:!bg-navy-700">
        <SearchIcon />
        <input
          placeholder={t("agents.searchAgentsPlaceholder")}
          className="!w-full rounded-xl text-sm font-medium outline-none dark:bg-navy-700 dark:text-white"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {filter && (
        <div className="w-[200px]">
          {filter}
        </div>
      )}
      {permissions.canCreateAgent && !isLoadingUsage && (
        <div className="relative" ref={dropdownRef}>
          {isAgentLimitReached ? (
            <TooltipHorizon
              trigger={
                <button
                  disabled={isAgentLimitReached}
                  className={`linear flex h-[45px] items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 text-white transition duration-200 ${
                    isAgentLimitReached
                      ? "cursor-not-allowed opacity-50"
                      : "hover:bg-brand-600 active:bg-brand-700"
                  }`}
                >
                  <MdAdd className="text-xl" />
                  <span className="flex whitespace-nowrap">
                    {t("common.create")}
                  </span>
                  <BiChevronDown
                    className={`text-xl transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
              }
              content={
                <span className="block max-w-[10rem] whitespace-normal text-center">
                  Agent limit reached. Please upgrade your plan to create more
                  agents.
                </span>
              }
              placement="top"
              extra=""
            />
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
              className="linear flex h-[45px] items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700"
            >
              <MdAdd className="text-xl" />
              <span className="flex whitespace-nowrap">
                {t("common.create")}
              </span>
              <BiChevronDown
                className={`text-xl transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          )}
          {isOpen && !isAgentLimitReached && (
            <div
              className="absolute right-0 top-full z-[9999] mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="py-1">
                {CREATE_AGENT_CATEGORIES_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick(option.value);
                    }}
                    className="flex w-full items-center px-4 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                  >
                    {t(option.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
};

export default Search;
